import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAppDispatch } from '../../store'
import { type ApplyFiltersAndDispatchParams, type Row } from '../../components/CustomTable/types'
import LoadingPage from '../../pages/LoadingPage'
import { getStudentsApi } from '../../store/slices/studentsSlice/apis'
import { getLoading, getStudentsList } from '../../store/slices/studentsSlice/selectors'
import DeleteFacultyAndStudent from '../FacultyAndStaff/DeleteFaculty'
import CreateAndEditStudents from './CreateAndEditStudents'
import { exportCsv } from '../../utils/exportCsv'
import { notification, closeNotification } from '../../components/Notifications'
import UserManager from '../../APIManager/UserManager'
import { useMutation } from '@tanstack/react-query'
import StudentsTopbarActions from './StudentsTopbarActions'
import ExportProcessingModal from '../../components/ExportProcessingModal'
import { useTaskStatus } from '../../hooks/useTaskStatus'
import { ExportBackgroundTaskRunningModal } from '../../components/ExportBackgroundTaskRunningModal'
import { useUserRole } from '../../hooks/useUserRole'
import ResetClassrankedAuthConfirmationModal from '../../components/ResetClassrankedAuthConfirmationModal'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

const Students = (): JSX.Element => {
  const isLoading = getLoading()
  const studentList = getStudentsList()
  const dispatch = useAppDispatch()

  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ scope: 'my' })
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportModalMessage, setExportModalMessage] = useState('')
  const { activeRole } = useUserRole()
  const showResetAction = activeRole?.roleType === 'GLOBAL'
  const [isResetPending, setIsResetPending] = useState(false)
  const [isResetModalOpen, setIsResetModalOpen] = useState(false)

  const {
    taskId: uploadTaskId,
    setTaskId: setUploadTaskId,
    showBackgroundModal: showUploadBlockedModal,
    setShowBackgroundModal: setShowUploadBlockedModal,
    isCheckingStatus: isUploadRunning
  } = useTaskStatus('uploadTask_students')

  const {
    taskId: exportTaskId,
    setTaskId: setExportTaskId,
    showBackgroundModal: showExportBlockedModal,
    setShowBackgroundModal: setShowExportBlockedModal,
    isCheckingStatus: isExportRunning,
    checkStatusBeforeExport
  } = useTaskStatus('exportTask_students')

  const isAnyTaskRunning = isUploadRunning || isExportRunning

  const openBlockedModal = (): void => {
    if (isUploadRunning) setShowUploadBlockedModal(true)
    else setShowExportBlockedModal(true)
  }

  const columns = studentList?.keys ?? []
  const rows: Row[] = studentList?.data ?? []
  const tableHeader = studentList?.header?.headerName ?? ''

  useEffect(() => {
    void dispatch(getStudentsApi({ params }))
  }, [params])

  const { mutate: exportStudentCSVMutate, isPending: exportLoading } = useMutation({
    mutationFn: UserManager.exportStudentsCSV,
    onMutate: () => notification('Preparing download...', { variant: 'loading', persist: true }),
    onSuccess: (response, _variables, context) => {
      if (response?.status === 202) {
        if (response?.data?.taskId) {
          setExportTaskId(response.data.taskId)
        }
        closeNotification(context)
        setExportModalMessage(response?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: context })
      } else {
        closeNotification(context)
      }
    },
    onError: (error: any, _variables, context) => {
      closeNotification(context)
      notification(error?.message ?? 'Export csv failed', { variant: 'error' })
    },
    meta: { silent: true }
  })

  const handleExport = async (): Promise<void> => {
    if (isUploadRunning) { openBlockedModal(); return }
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    const exportParams = {
      search: params?.search || '',
      sort: params?.sort || undefined
    }
    exportStudentCSVMutate(exportParams)
  }

  const handleResetClassrankedAuth = async (deleteForAll: boolean): Promise<void> => {
    setIsResetPending(true)
    try {
      const response = await UserManager.resetClassrankedAuth({ isStudent: true, delete_for_all: deleteForAll })
      notification(response?.data?.message ?? 'Cognito reset completed.', { variant: 'success' })
      setIsResetModalOpen(false)
      void dispatch(getStudentsApi({ params }))
    } catch (error: any) {
      notification(error?.message ?? 'Reset Classranked Auth failed', { variant: 'error' })
    } finally {
      setIsResetPending(false)
    }
  }

  const allowedActions = studentList?.actions ?? []

  const topbarActionsDropdownMenu = [
    {
      text: 'Export Students (.csv)',
      action: () => { void handleExport() },
      disabled: isAnyTaskRunning || exportLoading,
      visible: params?.scope !== 'all'
    },
    {
      text: 'Reset Classranked Authentication',
      action: () => { setIsResetModalOpen(true) },
      disabled: isResetPending || isAnyTaskRunning,
      visible: showResetAction
    }
  ]

  return (
    <>
    <Suspense fallback={<LoadingPage />}>
      <AppTable
        topbarTitle={tableHeader}
        customTopbarAction={
          <StudentsTopbarActions
            params={params}
            allowedActions={allowedActions}
            setUploadTaskId={setUploadTaskId}
            isTaskRunning={isAnyTaskRunning}
            onTaskBlock={openBlockedModal}
          />
        }
        topbarActionsDropdownMenu={topbarActionsDropdownMenu}
        exportButtonProps={{ loading: exportLoading || isAnyTaskRunning, disabled: exportLoading || isAnyTaskRunning }}
        list={studentList}
        rows={rows}
        loading={isLoading}
        columns={columns}
        featureName="Student"
        editAction={<CreateAndEditStudents params={params} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
        deleteAction={<DeleteFacultyAndStudent params={params} isStudent={true} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
        actionModalEntityDisplayField='firstName'
        params={params}
        setParams={setParams}
        showScopeToggle={true}
        scopeToggleTitle={tableHeader}
      />
    </Suspense>
    <ExportProcessingModal
      isOpen={exportModalOpen}
      onClose={() => { setExportModalOpen(false) }}
      message={exportModalMessage}
    />
    <ExportBackgroundTaskRunningModal
      isOpen={showExportBlockedModal}
      onClose={() => { setShowExportBlockedModal(false) }}
      taskId={exportTaskId ?? ''}
    />
    <ExportBackgroundTaskRunningModal
      isOpen={showUploadBlockedModal}
      onClose={() => { setShowUploadBlockedModal(false) }}
      taskId={uploadTaskId ?? ''}
    />
    <ResetClassrankedAuthConfirmationModal
      isOpen={isResetModalOpen}
      onClose={() => { setIsResetModalOpen(false) }}
      onConfirm={handleResetClassrankedAuth}
      isSubmitting={isResetPending}
      bodyText='This will remove ClassRanked authentication access for students on this tab who do not have any role in the ClassRanked system. Their records will remain in the system, and they can set up access again the next time they log in.'
    />
    </>
  )
}

export default Students
